import classnames from 'clsx';
import { useDebouncedValue } from '@mantine/hooks';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { LabelWithRequiredIndicator } from 'ui-components/components/label-with-required-indicator';
import { Text, TextType } from 'ui-components/components/text';
import { DimensionInput } from 'ui-components/components/dimension-input';
import { WeightInput } from 'ui-components/components/weight-input';
import { Input } from 'ui-components/components/input';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { SimpleModal } from '../../../../../components/simple-modal';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { FlexColumn, Gap } from 'ui-components/components/flex';

import type { Weight } from '@shipstation/client-common/types/weight';
import { convertWeight } from '../../../../../services/unit-services/weights/weight-convert-service';
import { messages, formMessages } from './messages';
import { useTrackWorkflow, WorkflowSteps } from '../../../../../tracking/use-workflow-tracking';
import { Workflows } from '../../../../../tracking/tracking-constants';
import Global from '../../../../../store/global-selectors';
import { useEnableCustomPackageWeights } from '../../../../../feature-flags/hooks/use-enable-custom-package-weights';
import { useBinaryState } from '../../../../../hooks/use-binary-state';
import { useIsSellerFeatureEnabled } from '../../../../../hooks/use-is-seller-feature-enabled';
import { PackingRecommendations } from '../../../../../constants/features';
import type { Dimensions } from '../../../../../prop-types/dimension-types.d';
import type { DimensionUnit } from '../../../../../prop-types/dimenstion-unit.d';
import { MODALS } from '../../constants';
import style from './custom-packages-modal.module.less';

type PackageInfo = {
  dimensions?: Dimensions;
  name?: string;
  tareWeight?: Weight | null;
  maxWeight?: Weight | null;
};

type NormalizedPackageInfo = {
  name?: string;
  dimensions?: Dimensions;
  tareWeight: number | null;
  maxWeight: number | null;
};
type CustomPackagesModalProps = {
  action: MODALS.Add | MODALS.Edit;
  onClose: () => void;
  onSave: (
    formValues: NormalizedPackageInfo,
    setErrorMessage: (value: string | null) => void,
    setIsSaving: (value: boolean) => void,
  ) => void;
  packageInfo?: PackageInfo;
  unit: string;
};

const METRIC_WEIGHT_UNITS = ['gram', 'grams'];

const SIZE_VALIDATION_DELAY_MILLISECONDS = 1000;

const WEIGHT_PLACEHOLDER_EXAMPLES = {
  metric: {
    tareWeight: { majorUnit: 2, minorUnit: 200 },
    maxWeight: { majorUnit: 6, minorUnit: 600 },
  },
  imperial: {
    tareWeight: { majorUnit: 2, minorUnit: 8 },
    maxWeight: { majorUnit: 6, minorUnit: 4 },
  },
};

const defaultPackageInfo: PackageInfo = {
  dimensions: {
    length: 0,
    width: 0,
    height: 0,
    unit: '' as DimensionUnit,
  },
  tareWeight: null,
  maxWeight: null,
  name: '',
};

export const CustomPackagesModal = ({ action, packageInfo = defaultPackageInfo, onClose, onSave, unit }: CustomPackagesModalProps) => {
  const intl = useIntl();
  const isWeightsEnabled = useEnableCustomPackageWeights();
  const isPackingRecommendationsEnabled = useIsSellerFeatureEnabled(PackingRecommendations);
  const shouldShowMaxWeight = isWeightsEnabled || isPackingRecommendationsEnabled;
  const sellerWeightUnit = useSelector(Global.selectSellerDefaultWeightUnit);
  const weightInputUnit = sellerWeightUnit?.toLowerCase();

  const normalizeWeight = (weightObj: Weight | null | undefined): number | null => {
    if (!weightObj || weightObj.value === null || weightObj.value === undefined) {
      return null;
    }

    const sourceUnit = weightObj.unit || sellerWeightUnit;
    const { value: convertedValue } = convertWeight({ value: weightObj.value, unit: sourceUnit }, sellerWeightUnit);

    return Math.round(convertedValue * 1000) / 1000;
  };

  const normalizeDimensions = (dims: Dimensions | undefined): Dimensions | undefined => {
    if (!dims) return dims;
    return {
      ...dims,
      length: dims.length ? parseFloat(dims.length.toFixed(2)) : dims.length,
      width: dims.width ? parseFloat(dims.width.toFixed(2)) : dims.width,
      height: dims.height ? parseFloat(dims.height.toFixed(2)) : dims.height,
      unit: (dims.unit || unit) as DimensionUnit,
    };
  };

  const normalizedPackageInfo: NormalizedPackageInfo = {
    ...packageInfo,
    tareWeight: normalizeWeight(packageInfo.tareWeight),
    maxWeight: normalizeWeight(packageInfo.maxWeight),
    dimensions: normalizeDimensions(packageInfo.dimensions),
  };

  const [formValues, setFormValues] = useAsyncSafeState(normalizedPackageInfo);
  const [isMaxWeightTouched, markMaxWeightTouched] = useBinaryState();
  const isMaxWeightValid = !shouldShowMaxWeight || formValues.maxWeight > 0;
  const shouldShowMaxWeightValidation = !isMaxWeightValid && (isMaxWeightTouched || action === MODALS.Edit);
  const [isSizeTouched, markSizeTouched] = useBinaryState();
  const isSizeValid = formValues.dimensions?.length > 0 && formValues.dimensions?.width > 0 && formValues.dimensions?.height > 0;
  const [settledDimensions] = useDebouncedValue(formValues.dimensions, SIZE_VALIDATION_DELAY_MILLISECONDS);
  const shouldShowSizeValidation = !isSizeValid && settledDimensions === formValues.dimensions && (isSizeTouched || action === MODALS.Edit);
  const placeholderExamples = METRIC_WEIGHT_UNITS.includes(weightInputUnit)
    ? WEIGHT_PLACEHOLDER_EXAMPLES.metric
    : WEIGHT_PLACEHOLDER_EXAMPLES.imperial;
  const formatPlaceholder = (value: number) => intl.formatMessage(formMessages['form-placeholder-example'], { value });
  const [isSaving, setIsSaving] = useAsyncSafeState(false);
  const [errorMessage, setErrorMessage] = useAsyncSafeState(null);
  const trackCustomPackageFlow = useTrackWorkflow(Workflows.CUSTOM_PACKAGE_FLOW);

  const handleOnSave = useCallback(() => {
    onSave(formValues, setErrorMessage, setIsSaving);
  }, [formValues, onSave, setErrorMessage, setIsSaving]);

  const handleNameChange = useCallback(
    (name: string) => {
      setFormValues({ ...formValues, name });
      if (errorMessage) {
        setErrorMessage(null);
      }
    },
    [formValues, setFormValues, errorMessage, setErrorMessage],
  );

  const onCancel = useCallback(() => {
    onClose();
    trackCustomPackageFlow(WorkflowSteps.CANCEL);
  }, [onClose, trackCustomPackageFlow]);

  return (
    <SimpleModal
      actionMessage={messages.save}
      cancelMessage={messages.cancel}
      className={style.modal}
      headerMessage={messages[`header-${action}`]}
      isActionDisabled={isSaving || !formValues.name || !isSizeValid || !isMaxWeightValid}
      onAction={handleOnSave}
      onCancel={onCancel}
      onClose={onClose}
      size='small'
    >
      {errorMessage && (
        <BannerMessageV2
          className={style.errorBanner}
          content={<Text textType={TextType.Paragraph} value={errorMessage} />}
          status={BannerStatus.Error}
        />
      )}
      <Text className={style.description} textType={TextType.Description} value={messages.description} />
      <div className={style.name}>
        <LabelWithRequiredIndicator>
          <Text textType={TextType.Label} value={formMessages['form-label-name']} />
        </LabelWithRequiredIndicator>
        <Input hasError={!!errorMessage} onChange={handleNameChange} type='text' value={formValues.name} />
      </div>
      <div className={style.sizeDimensions}>
        <LabelWithRequiredIndicator>
          <Text textType={TextType.Label} value={formMessages['form-label-dimensions']} />
        </LabelWithRequiredIndicator>
        <DimensionInput
          dimensions={formValues.dimensions}
          onChange={(dimensions) => {
            markSizeTouched();
            setFormValues({ ...formValues, dimensions });
          }}
          unit={unit}
        />
        {shouldShowSizeValidation && (
          <Text
            className={classnames(style.validationText, style.sizeValidationText)}
            textType={TextType.Caption}
            value={formMessages['form-validation-size']}
          />
        )}
      </div>
      {shouldShowMaxWeight && (
        <FlexColumn gap={Gap.Space16}>
          {isWeightsEnabled && (
            <FlexColumn gap={Gap.Space4}>
              <Text textType={TextType.Label} value={formMessages['form-label-tare-weight']} />
              <Text className={style.helperText} textType={TextType.Caption} value={formMessages['form-helper-tare-weight']} />
              <WeightInput
                majorUnitPlaceholder={formatPlaceholder(placeholderExamples.tareWeight.majorUnit)}
                minorUnitPlaceholder={formatPlaceholder(placeholderExamples.tareWeight.minorUnit)}
                onChange={(tareWeight) => setFormValues({ ...formValues, tareWeight })}
                unit={weightInputUnit}
                weight={formValues.tareWeight}
              />
            </FlexColumn>
          )}
          <FlexColumn gap={Gap.Space4}>
            <LabelWithRequiredIndicator>
              <Text textType={TextType.Label} value={formMessages['form-label-max-weight']} />
            </LabelWithRequiredIndicator>
            <Text className={style.helperText} textType={TextType.Caption} value={formMessages['form-helper-max-weight']} />
            <WeightInput
              majorUnitPlaceholder={formatPlaceholder(placeholderExamples.maxWeight.majorUnit)}
              minorUnitPlaceholder={formatPlaceholder(placeholderExamples.maxWeight.minorUnit)}
              onChange={(maxWeight) => {
                markMaxWeightTouched();
                setFormValues({ ...formValues, maxWeight });
              }}
              unit={weightInputUnit}
              weight={formValues.maxWeight}
            />
            {shouldShowMaxWeightValidation && (
              <Text className={style.validationText} textType={TextType.Caption} value={formMessages['form-validation-max-weight']} />
            )}
          </FlexColumn>
        </FlexColumn>
      )}
    </SimpleModal>
  );
};
